/**
 * Pure, React-free helpers for the Blocker domain: id generation, blank/edit
 * draft factories, the daily-update → blocker conversion, validation, blocking
 * "age" computation + the "BLOCKED — N DAYS" label, scope/status metadata,
 * board sorting, and date formatting.
 *
 * Deliberately NO productivity/velocity/leaderboard metric: a blocker measures
 * an impediment and its delivery risk — never ranks people.
 */

import { v4 as uuid } from 'uuid';
import type { BadgeTone } from '../../ui';
import type { DailyUpdateSummary } from '../dailyUpdates/types';
import { todayIso } from '../dailyUpdates/dailyUpdateModel';
import type {
  Blocker,
  BlockerDraft,
  BlockerFilter,
  BlockerScope,
  BlockerStatus,
  BlockerSummary,
} from './types';

/** Deterministic document id for a new blocker. */
export function blockerDocId(): string {
  return `blk_${uuid()}`;
}

/** Creates an empty draft for the create form (optionally seeding fields). */
export function blankBlockerDraft(
  blockedEngineer = '',
  seed: Partial<BlockerDraft> = {},
): BlockerDraft {
  return {
    summary: '',
    blockedStoryId: undefined,
    blockedWorkstreamId: undefined,
    blockedEngineer,
    dependencyOwner: '',
    internalExternal: 'INTERNAL',
    blockingSince: todayIso(),
    expectedResolution: undefined,
    escalationOwner: undefined,
    relatedTicket: undefined,
    affectedGoalId: undefined,
    forecastImpactDays: undefined,
    status: 'OPEN',
    ...seed,
  };
}

/** Projects an existing blocker into an editable draft. */
export function draftFromBlocker(blocker: Blocker): BlockerDraft {
  return {
    summary: blocker.summary,
    blockedStoryId: blocker.blockedStoryId,
    blockedWorkstreamId: blocker.blockedWorkstreamId,
    blockedEngineer: blocker.blockedEngineer,
    dependencyOwner: blocker.dependencyOwner,
    internalExternal: blocker.internalExternal,
    blockingSince: blocker.blockingSince,
    expectedResolution: blocker.expectedResolution,
    escalationOwner: blocker.escalationOwner,
    relatedTicket: blocker.relatedTicket,
    affectedGoalId: blocker.affectedGoalId,
    forecastImpactDays: blocker.forecastImpactDays,
    status: blocker.status,
  };
}

/**
 * Builds a blocker draft from a daily update that marked a blocker — the
 * "offer to convert" path. Carries over the blocked engineer, the linked
 * story / workstream / goal, and the free-text blocker as the summary; seeds
 * "blocking since" from the update's day. The human still supplies the
 * dependency owner + confirms scope/impact before saving (HUMAN-VERIFY).
 */
export function draftFromDailyUpdate(update: DailyUpdateSummary): BlockerDraft {
  return blankBlockerDraft(update.engineer, {
    summary: (update.blocker ?? '').trim(),
    blockedStoryId: update.storyId,
    blockedWorkstreamId: update.workstreamId,
    affectedGoalId: update.goalId,
    blockingSince: update.date || todayIso(),
  });
}

/**
 * Clamps a raw forecast-impact input to a non-negative integer number of days,
 * or undefined when blank / non-numeric.
 */
export function normalizeImpactDays(
  raw: string | number | undefined,
): number | undefined {
  if (raw === undefined || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return undefined;
  return Math.max(0, Math.round(n));
}

/** True when a string is a recognized scope. */
export function isBlockerScope(value: string): value is BlockerScope {
  return value === 'INTERNAL' || value === 'EXTERNAL';
}

/** True when a string is a recognized status. */
export function isBlockerStatus(value: string): value is BlockerStatus {
  return value === 'OPEN' || value === 'RESOLVED';
}

/**
 * Validates a draft for save. Returns a list of human-readable problems; an
 * empty list means the draft is valid.
 */
export function validateBlockerDraft(draft: BlockerDraft): string[] {
  const problems: string[] = [];
  if (!draft.summary.trim()) problems.push('A blocker summary is required.');
  if (!draft.blockedEngineer.trim()) problems.push('A blocked engineer is required.');
  if (!draft.dependencyOwner.trim()) problems.push('A dependency owner is required.');
  if (!isBlockerScope(draft.internalExternal)) {
    problems.push('Choose internal or external.');
  }
  if (!draft.blockingSince.trim()) problems.push('A "blocking since" date is required.');
  if (
    draft.forecastImpactDays !== undefined &&
    (!Number.isFinite(draft.forecastImpactDays) || draft.forecastImpactDays < 0)
  ) {
    problems.push('Forecast impact must be a non-negative number of days.');
  }
  return problems;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Whole days a blocker has been blocking (from blockingSince until now, floored
 * at 0). Returns 0 when the date is unparseable.
 */
export function ageDays(
  blocker: Pick<Blocker, 'blockingSince'> | Pick<BlockerSummary, 'blockingSince'>,
): number {
  const d = new Date(`${blocker.blockingSince}T00:00:00`);
  if (Number.isNaN(d.getTime())) return 0;
  const diff = Math.floor((Date.now() - d.getTime()) / MS_PER_DAY);
  return Math.max(0, diff);
}

/** The "BLOCKED — N DAYS" board label (or "RESOLVED" for a resolved blocker). */
export function ageLabel(
  blocker: Pick<Blocker, 'blockingSince' | 'status'> | Pick<BlockerSummary, 'blockingSince' | 'status'>,
): string {
  if (blocker.status === 'RESOLVED') return 'RESOLVED';
  const n = ageDays(blocker);
  return `BLOCKED — ${n} ${n === 1 ? 'DAY' : 'DAYS'}`;
}

/** Badge tone for the age, escalating with how long it has been blocking. */
export function ageTone(
  blocker: Pick<Blocker, 'blockingSince' | 'status'> | Pick<BlockerSummary, 'blockingSince' | 'status'>,
): BadgeTone {
  if (blocker.status === 'RESOLVED') return 'success';
  const n = ageDays(blocker);
  if (n >= 5) return 'danger';
  if (n >= 2) return 'warning';
  return 'info';
}

/** Human-readable label for a scope. */
export function scopeLabel(scope: BlockerScope): string {
  return scope === 'EXTERNAL' ? 'External' : 'Internal';
}

/** Badge tone for a scope. */
export function scopeTone(scope: BlockerScope): BadgeTone {
  return scope === 'EXTERNAL' ? 'warning' : 'neutral';
}

/** Human-readable label for a status. */
export function statusLabel(status: BlockerStatus): string {
  return status === 'RESOLVED' ? 'Resolved' : 'Open';
}

/** Badge tone for a status. */
export function statusTone(status: BlockerStatus): BadgeTone {
  return status === 'RESOLVED' ? 'success' : 'danger';
}

/** True when the blocker is still open. */
export function isOpen(
  blocker: Pick<Blocker, 'status'> | Pick<BlockerSummary, 'status'>,
): boolean {
  return blocker.status === 'OPEN';
}

/** Applies a status / scope filter (empty fields match everything). */
export function filterBlockers(
  items: BlockerSummary[],
  filter: BlockerFilter,
): BlockerSummary[] {
  const status = filter.status.trim();
  const scope = filter.scope.trim();
  return items.filter((b) => {
    if (status && b.status !== status) return false;
    if (scope && b.internalExternal !== scope) return false;
    return true;
  });
}

/**
 * Sorts blockers for the board: OPEN before RESOLVED, then longest-blocking
 * first (oldest blockingSince), then most-recently-updated.
 */
export function sortBlockers(items: BlockerSummary[]): BlockerSummary[] {
  return items.slice().sort((a, b) => {
    if (a.status !== b.status) return a.status === 'OPEN' ? -1 : 1;
    // Older blockingSince = longer blocked = higher priority.
    const bySince = (a.blockingSince ?? '').localeCompare(b.blockingSince ?? '');
    if (bySince !== 0) return bySince;
    return (b.lastUpdated ?? '').localeCompare(a.lastUpdated ?? '');
  });
}

/** Formats a yyyy-mm-dd (or ISO) date as a compact local date. */
export function formatDate(date?: string): string {
  if (!date) return '';
  const d = new Date(date.length === 10 ? `${date}T00:00:00` : date);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
